import { api } from "@/shared/api";
import { API_ENDPOINTS } from "@/shared/api";
import { useAuthStore } from "@/features/auth/model";
import type { AuthAccountOption, LoginApiResult, PasswordResetRequestResult, User } from "../types";

/**
 * Auth service
 *
 * Business logic for authentication.
 * Uses abstracted API client - backend-agnostic.
 */

interface LoginRequest {
  email: string;
  password: string;
  accountId?: string;
  tokenName?: string;
}

interface LoginTokenApiResponse {
  token: string;
  user: {
    id: string;
    email: string;
    isEmailVerified: boolean;
    createdAt: string;
    updatedAt: string;
    user?: {
      id: string;
      role: string;
      accountId?: string;
    };
  };
}

interface MeResponse {
  id: string;
  email: string;
  isEmailVerified: boolean;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    role: string;
    accountId?: string;
    locationId?: string | null;
    preferredLanguage?: string | null;
    firstName?: string;
    lastName?: string;
  };
}

function parseAuthAccounts(raw: unknown): AuthAccountOption[] {
  if (!Array.isArray(raw)) return [];
  const out: AuthAccountOption[] = [];
  for (const item of raw) {
    if (typeof item !== "object" || item === null) continue;
    const row = item as Record<string, unknown>;
    if (typeof row.accountId !== "string") continue;
    out.push({
      accountId: row.accountId,
      accountName:
        row.accountName === null || row.accountName === undefined ? null : String(row.accountName),
      userName: typeof row.userName === "string" ? row.userName : "",
      role: typeof row.role === "string" ? row.role : "",
    });
  }
  return out;
}

function isAccountSelectionPayload(
  data: unknown
): data is { requiresAccountSelection: true; accounts: unknown } {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as { requiresAccountSelection?: boolean }).requiresAccountSelection === true &&
    "accounts" in data
  );
}

async function completeLoginAfterToken(response: LoginTokenApiResponse): Promise<LoginApiResult> {
  let fullUser: MeResponse;
  try {
    const tempToken = response.token;
    const originalToken = localStorage.getItem("auth-storage");
    if (originalToken) {
      const parsed = JSON.parse(originalToken) as { state?: { token?: string } };
      parsed.state = { ...parsed.state, token: tempToken };
      localStorage.setItem("auth-storage", JSON.stringify(parsed));
    } else {
      localStorage.setItem(
        "auth-storage",
        JSON.stringify({ state: { token: tempToken, user: null, isAuthenticated: false } })
      );
    }

    const { data: meData } = await api.post<MeResponse>(API_ENDPOINTS.AUTH.me(), {});
    fullUser = meData;
  } catch {
    return { type: "error", message: "Login failed. Please try again." };
  }

  const emailPart = fullUser.email?.split("@")[0] ?? "";
  const [derivedFirst, ...derivedLastParts] = emailPart.includes(".")
    ? emailPart.split(".")
    : [emailPart, ""];
  const derivedLastName =
    derivedLastParts.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(" ") || "";
  const user: User = {
    id: fullUser.user?.id ?? fullUser.id,
    email: fullUser.email,
    firstName: (fullUser.user?.firstName ?? "").trim() || derivedFirst || "User",
    lastName: (fullUser.user?.lastName ?? "").trim() || derivedLastName,
    role: fullUser.user?.role,
    tenant_id: fullUser.user?.accountId,
    locationId: fullUser.user?.locationId ?? undefined,
    preferredLanguage: fullUser.user?.preferredLanguage ?? undefined,
    createdAt: fullUser.createdAt,
    updatedAt: fullUser.updatedAt,
  };

  return {
    type: "success",
    user,
    token: response.token,
  };
}

export const authApi = {
  /**
   * Login with email and password. Pass `accountId` when completing multi-account selection.
   */
  async login(email: string, password: string, accountId?: string): Promise<LoginApiResult> {
    try {
      const loginData: LoginRequest = {
        email,
        password,
        ...(accountId ? { accountId } : {}),
      };

      const { data } = await api.post<unknown>(API_ENDPOINTS.AUTH.login(), loginData);

      if (isAccountSelectionPayload(data)) {
        const accounts = parseAuthAccounts(data.accounts);
        if (accounts.length === 0) {
          return { type: "error", message: "Login failed. Please check your credentials." };
        }
        return { type: "account_selection", accounts };
      }

      const response = data as LoginTokenApiResponse;
      if (!response?.token || typeof response.token !== "string") {
        return { type: "error", message: "Login failed. Please check your credentials." };
      }

      return completeLoginAfterToken(response);
    } catch (error: unknown) {
      if (error && typeof error === "object" && "message" in error) {
        const apiError = error as { message: string; status?: number };
        return {
          type: "error",
          message: apiError.message || "Login failed",
        };
      }

      return {
        type: "error",
        message: "Login failed. Please check your credentials.",
      };
    }
  },

  /**
   * Authenticate using magic link code (for Location Users)
   * Returns user, token, and error (if any)
   */
  async loginWithMagicLink(
    code: string
  ): Promise<{ user: User | null; token: string | null; error: string | null }> {
    try {
      const { data: response } = await api.post<{
        user: {
          id: string;
          name: string;
          firstName?: string;
          lastName?: string;
          role: string;
          accountId: string | null;
          locationId: string | null;
          authUser: {
            id: string;
            email: string | null;
            createdAt: string;
            updatedAt: string;
          };
        };
        token: string;
      }>(API_ENDPOINTS.USERS.magicLinkAuthenticate(), { code });

      let fullUser: MeResponse;
      try {
        const tempToken = response.token;
        const originalToken = localStorage.getItem("auth-storage");
        if (originalToken) {
          const parsed = JSON.parse(originalToken);
          parsed.state = { ...parsed.state, token: tempToken };
          localStorage.setItem("auth-storage", JSON.stringify(parsed));
        } else {
          localStorage.setItem(
            "auth-storage",
            JSON.stringify({ state: { token: tempToken, user: null, isAuthenticated: false } })
          );
        }

        const { data: meData } = await api.post<MeResponse>(API_ENDPOINTS.AUTH.me(), {});
        fullUser = meData;
      } catch {
        return {
          user: null,
          token: null,
          error: "Magic link authentication failed. The link may be invalid or expired.",
        };
      }

      const fallbackPart = fullUser.email?.split("@")[0] || "Location User";
      const [fallbackFirst, ...fallbackLastParts] = fallbackPart.includes(" ")
        ? fallbackPart.split(" ")
        : [fallbackPart, ""];
      const user: User = {
        id: fullUser.user?.id ?? fullUser.id,
        email: fullUser.email || response.user.authUser.email || "",
        firstName: fullUser.user?.firstName ?? response.user.firstName ?? fallbackFirst,
        lastName:
          fullUser.user?.lastName ?? response.user.lastName ?? (fallbackLastParts.join(" ") || ""),
        role: fullUser.user?.role ?? response.user.role,
        tenant_id: fullUser.user?.accountId ?? response.user.accountId ?? "",
        locationId: fullUser.user?.locationId ?? response.user.locationId ?? undefined,
        preferredLanguage: fullUser.user?.preferredLanguage ?? undefined,
        createdAt: fullUser.createdAt,
        updatedAt: fullUser.updatedAt,
      };

      return {
        user,
        token: response.token,
        error: null,
      };
    } catch (error: unknown) {
      if (error && typeof error === "object" && "message" in error) {
        const apiError = error as { message: string; status?: number };
        return {
          user: null,
          token: null,
          error: apiError.message || "Magic link authentication failed",
        };
      }

      return {
        user: null,
        token: null,
        error: "Magic link authentication failed. The link may be invalid or expired.",
      };
    }
  },

  /**
   * Request a password reset email. Pass `accountId` when completing multi-account selection.
   */
  async requestPasswordReset(
    email: string,
    accountId?: string
  ): Promise<PasswordResetRequestResult> {
    try {
      const { data } = await api.post<unknown>(API_ENDPOINTS.AUTH.forgotPassword(), {
        email,
        ...(accountId ? { accountId } : {}),
      });

      if (isAccountSelectionPayload(data)) {
        const accounts = parseAuthAccounts(data.accounts);
        if (accounts.length === 0) {
          return { type: "failure", error: null, isRateLimited: false };
        }
        return { type: "account_selection", accounts };
      }

      return { type: "success" };
    } catch (error: unknown) {
      if (
        error &&
        typeof error === "object" &&
        "status" in error &&
        (error as { status: number }).status === 429
      ) {
        const msg =
          error && typeof error === "object" && "message" in error
            ? (error as { message: string }).message
            : null;
        return { type: "failure", error: msg, isRateLimited: true };
      }
      return { type: "failure", error: null, isRateLimited: false };
    }
  },

  /**
   * Logout: invalidate token on backend, then clear all local session data.
   * Call this for user-initiated logout (not for 401 auto-logout).
   */
  async logout(): Promise<void> {
    try {
      await api.post(API_ENDPOINTS.AUTH.logout());
    } catch {
      // Ignore - we're logging out anyway
    } finally {
      useAuthStore.getState().logout();
    }
  },

  /**
   * Validate reset-password token from email link (public; no auth required).
   */
  async validateResetPasswordToken(token: string): Promise<{ email: string }> {
    const { data } = await api.get<{ email: string }>(API_ENDPOINTS.AUTH.resetPasswordValidate(), {
      token,
    });
    return data;
  },

  /**
   * Reset password with token from email link.
   */
  async resetPassword(
    token: string,
    password: string
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      await api.post<{ message: string }>(API_ENDPOINTS.AUTH.resetPassword(), { token, password });
      return { success: true, error: null };
    } catch (error: unknown) {
      const message =
        error && typeof error === "object" && "message" in error
          ? (error as { message: string }).message
          : null;
      return { success: false, error: message ?? null };
    }
  },
};
